import React from 'react';

import { Trans } from '@lingui/macro';
import makeStyles from '@mui/styles/makeStyles';
import Grid from '@mui/material/Grid';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import YouTubeIcon from '@mui/icons-material/YouTube';

import { fetchYtTitles } from '../../utils/ytdlp';
import { fetchYoutubeSnippet } from '../../utils/youtube';

import TabPanel from '../TabPanel';
import TabsHorizontal from '../TabsHorizontal';

const useStyles = makeStyles((theme) => ({
	root: {
		'& .MuiOutlinedInput-input': {
			whiteSpace: 'pre-line',
		},
	},
}));

function init(settings) {
	const initSettings = {
		name: 'Livestream',
		description: 'Live from earth. Powered by datarhei Restreamer.',
		youtube_id: '',
		youtube_url: '',
		author: {},
		...settings,
	};

	initSettings.author = {
		name: '',
		description: '',
		...initSettings.author,
	};

	return initSettings;
}

const extractYouTubeVideoId = (url) => {
	if (!url) return '';
	const trimmed = url.trim();
	const patterns = [
		/[?&]v=([a-zA-Z0-9_-]{11})(?:[&?/]|$)/,
			/youtu\.be\/([a-zA-Z0-9_-]{11})(?:[?&\/]|$)/,
			/\/(?:live|embed|shorts|v)\/([a-zA-Z0-9_-]{11})(?:[?&\/]|$)/,
	];
	for (const pattern of patterns) {
		const match = trimmed.match(pattern);
		if (match) return match[1];
	}
	if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) return trimmed;
	return '';
};

export default function Control(props) {
	const classes = useStyles();
	const [$tab, setTab] = React.useState('content');
	const settings = init(props.settings);
	const [fetching, setFetching] = React.useState(false);
	const [fetchError, setFetchError] = React.useState('');
	const youtubeUrlRef = React.useRef(null);

	const handleFetchTitles = async () => {
		setFetchError('');
		let videoId = settings.youtube_id || (props.settings && props.settings.youtube_id) || '';
		if (!videoId) {
			const url = settings.youtube_url || (props.settings && props.settings.youtube_url) || '';
			if (url) {
				videoId = extractYouTubeVideoId(url);
				if (videoId) settings.youtube_id = videoId;
			}
		}
		if (!videoId) {
			setFetchError('No YouTube ID found in metadata — introduce la URL en el campo de YouTube arriba.');
			try { youtubeUrlRef.current && youtubeUrlRef.current.focus(); } catch (e) {}
			return;
		}
		setFetching(true);
		try {
			// Prefer YouTube Data API on the client (runtime config or REACT_APP var)
			const _rt = (typeof window !== 'undefined' && window.__RESTREAMER_CONFIG__) || {};
			const ytApiKey = _rt.YOUTUBE_API_KEY || _rt.YT_API_KEY || process.env.REACT_APP_YOUTUBE_API_KEY || '';
			let t = null;
			if (ytApiKey) {
				try {
					t = await fetchYoutubeSnippet(videoId, ytApiKey, 10000);
				} catch (err) {
					console.warn('[youtube api] failed:', err && err.message ? err.message : err);
				}
			}

			// Fallback to yt-dlp titles/stream extractor if YouTube API is not available or fails
			if (!t) {
				const d = await fetchYtTitles(videoId);
				if (d && (d.title || d.description)) t = d;
			}

			if (t && t.title) settings.name = t.title;
			if (t && t.description) settings.description = t.description;
			settings.youtube_id = videoId;
			props.onChange(settings, false);
		} catch (e) {
			setFetchError(e && e.message ? e.message : 'Failed to fetch titles');
		} finally {
			setFetching(false);
		}
	};

	// Set the defaults
	React.useEffect(() => {
		props.onChange(settings, true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleChange = (what) => (event) => {
		const value = event.target.value;

		if (what === 'author.name') {
			settings.author.name = value;
		} else if (what === 'author.description') {
			settings.author.description = value;
		} else {
			settings[what] = value;
		}

		props.onChange(settings, false);
	};

	const handleChangeTab = (event, value) => {
		setTab(value);
	};

	return (
		<Grid container spacing={0}>
			<Grid item xs={12}>
				<TabsHorizontal value={$tab} onChange={handleChangeTab}>
					<Tab className="tab" label={<Trans>Content</Trans>} value="content" />
					<Tab className="tab" label={<Trans>Author</Trans>} value="author" />
				</TabsHorizontal>
				<TabPanel value={$tab} index="content">
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<TextField
								variant="outlined"
								fullWidth
								label={<Trans>YouTube URL</Trans>}
								placeholder="https://www.youtube.com/watch?v=VIDEO_ID"
								inputRef={youtubeUrlRef}
								value={settings.youtube_url || ''}
								onChange={handleChange('youtube_url')}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								variant="outlined"
								fullWidth
								label={<Trans>Name</Trans>}
								value={settings.name}
								onChange={handleChange('name')}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<Tooltip title={<Trans>Fetch title & description from stored YouTube ID</Trans>}>
												<span>
													<Button
														size="small"
														variant="outlined"
														startIcon={fetching ? <CircularProgress size={16} color="inherit" /> : <YouTubeIcon />}
														onClick={handleFetchTitles}
														disabled={
														fetching ||
														!(settings.youtube_id || (settings.youtube_url && extractYouTubeVideoId(settings.youtube_url)))
													}
														style={{ textTransform: 'none' }}
													>
														{fetching ? <Trans>Fetching...</Trans> : <Trans>Fetch from stream</Trans>}
													</Button>
												</span>
											</Tooltip>
										</InputAdornment>
									),
								}}
							/>
							{fetchError && (
								<div style={{ marginTop: 6 }}>
									<span style={{ color: '#f44336', fontSize: '0.8rem' }}>{fetchError}</span>
								</div>
							)}
						</Grid>
						<Grid item xs={12}>
							<TextField
								className={classes.root}
								variant="outlined"
								fullWidth
								multiline
								rows={10}
								label={<Trans>Description</Trans>}
								value={settings.description}
								onChange={handleChange('description')}
							/>
						</Grid>
					</Grid>
				</TabPanel>
				<TabPanel value={$tab} index="author">
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<TextField
								variant="outlined"
								fullWidth
								label={<Trans>Name</Trans>}
								value={settings.author.name}
								onChange={handleChange('author.name')}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								className={classes.root}
								variant="outlined"
								fullWidth
								multiline
								rows={10}
								label={<Trans>Description</Trans>}
								value={settings.author.description}
								onChange={handleChange('author.description')}
							/>
						</Grid>
					</Grid>
				</TabPanel>
			</Grid>
		</Grid>
	);
}

Control.defaultProps = {
	settings: {},
	onChange: function (metadata) {},
};
